import React from 'react';
import { compose } from 'recompose';
import { Trans } from 'react-i18next';
import { SignIn as AmplifySignIn } from 'aws-amplify-react/lib/Auth';
import { Button } from '@material-ui/core';
import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core/styles';
import { Formik, Form } from 'formik';

import { ICognitoError } from 'types/aws-amplify';
import { ForgotPassword, SignUp } from 'Links';
import { withNotify, IWithNotify } from 'providers';
import {
  ActionContainer,
  SubmitButton,
  Layouts,
  ValidatedTextField,
  FormikEffect,
  VisibilityAdornment,
} from 'components';

import { Main, ClientSidebarHeader } from '../components';
import { SignInSchema, ISignInValues } from '../validations';

interface IPropsEnhanced extends IWithNotify, WithStyles<typeof styles> {}

interface IState {
  working: boolean;
  passwordVisible: boolean;
}

const styles = (theme: Theme) =>
  createStyles({
    button: {
      padding: 0,
      marginTop: theme.spacing(1),
    },
  });

const enhancers = compose<IPropsEnhanced, {}>(withNotify, withStyles(styles));

class SignIn extends AmplifySignIn<IPropsEnhanced, IState> {
  state: IState = {
    working: false,
    passwordVisible: false,
  };

  handleChange = ({ username, password }: ISignInValues) => {
    const { updateUsername, updatePassword } = this.props;
    updateUsername(username.toLowerCase());
    updatePassword(password);
  };

  onSubmit = async (values: ISignInValues) => {
    const sanitizedValues = { ...values, username: values.username.toLowerCase() };

    // Parent AuthPiece uses this.inputs object
    Object.assign(this.inputs, sanitizedValues);

    this.setState({ working: true }, () => {
      // Parent AWS component requires an event
      this.signIn(new CustomEvent('dummy'));
    });
  };

  error = (error: ICognitoError) => {
    const { notify } = this.props;

    const message = this.errorMessage(error);
    this.setState({ working: false });
    notify.error({ message });
  };

  renderSidebar() {
    const { classes, authData } = this.props;
    const { working, passwordVisible } = this.state;

    const initialValues: ISignInValues = {
      username: authData.username,
      password: '',
    };

    return (
      <Formik
        onSubmit={this.onSubmit}
        initialValues={initialValues}
        validationSchema={SignInSchema}>
        {() => (
          <Form>
            <FormikEffect onChange={this.handleChange} delay={100} />
            <ClientSidebarHeader i18nKey="auth:sidebarHeader" />

            <ValidatedTextField name="username" i18nKey="user:email" />
            <ValidatedTextField
              name="password"
              type={passwordVisible ? 'text' : 'password'}
              i18nKey="user:password"
              InputProps={{
                endAdornment: (
                  <VisibilityAdornment
                    visible={passwordVisible}
                    onToggle={() => this.setState({ passwordVisible: !passwordVisible })}
                  />
                ),
              }}
            />

            <Button component={ForgotPassword} className={classes.button}>
              <Trans i18nKey="auth:signInForgotPassword" />
            </Button>

            <ActionContainer>
              <SubmitButton working={working} text={<Trans i18nKey="auth:signInButton" />} />
              <Button component={SignUp} fullWidth>
                <Trans i18nKey="auth:signInSignUp" />
              </Button>
            </ActionContainer>
          </Form>
        )}
      </Formik>
    );
  }

  showComponent() {
    return (
      <Layouts.Full2Column
        sidebar={this.renderSidebar()}
        main={<Main headerI18nKey="auth:bodyHeader1" subHeaderI18nKey="auth:signInSubHeader" />}
      />
    );
  }
}

// @ts-ignore - TODO: AWS Components are untyped
export default enhancers(SignIn);
